import { Autocomplete, Box, InputAdornment, TextField, Typography } from "@mui/material";
import Search from "@mui/icons-material/Search";
import { useAsync2 } from "nate-react-api-helpers";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { shortDate } from "../../../misc/dates";
import { modKey } from "../../../misc/keys";
import { useDebounced } from "../../../misc/useDebounced";
import { useWorkspace } from "../../../misc/Workspace";
import { ProjectCode } from "../../project/ProjectCode";
import { searchReady } from "../TaskPicker";
import { pagePath } from "../taskUrl";

// minHeaderSearch is lower than the pickers' minimum: the header search is where a user
// looks a task up by a word or two, and a page of 10 keeps a broad match short
const minHeaderSearch = 2;

// SearchField is the task search in the desktop app bar. Picking a task opens it where
// it sits on the calendar; enter with nothing picked opens the search page.
export function SearchField() {
    const ws = useWorkspace();
    const navigate = useNavigate();
    const location = useLocation();
    const input = useRef<HTMLInputElement>(null);
    const [text, setText] = useState("");

    const term = useDebounced(text.trim(), 200);
    const ready = searchReady(term, minHeaderSearch);

    const options = useAsync2(async (input: { search: string; ready: boolean; team: number }) => {
        if (!input.ready) return [] as Task[];

        const page = await api.tasks.listPage({ search: input.search, team: input.team || undefined, limit: 10 });
        return page.data;
    }, { search: term, ready: ready, team: ws.team }, [term, ready, ws.team]);

    // ctrl/cmd+k is the way to the field from anywhere
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;

            e.preventDefault();
            input.current?.focus();
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const open = (task: Task) => {
        const current = pagePath(location.pathname);
        const onDay = current.endsWith("/tasks/day");
        const onWeek = current.endsWith("/tasks/calendar");
        const params = new URLSearchParams(onDay || onWeek ? location.search : "");

        for (const key of ["project", "goal", "fromTask", "panel"]) params.delete(key);
        params.set("task", String(task.id));

        // the day page keeps its date; the week page moves to the task's
        if (!onDay && task.dueDate) params.set("date", task.dueDate);
        if (!onDay && !onWeek) params.set("mode", "week");

        const page = onDay ? "/tasks/day" : "/tasks/calendar";
        navigate(ws.path(page) + "?" + params.toString());
    };

    return (
        <Autocomplete
            freeSolo
            size="small"
            options={options.result || []}
            filterOptions={list => list}
            value={null}
            inputValue={text}
            onInputChange={(_, value, reason) => {
                if (reason !== "reset") setText(value);
            }}
            getOptionLabel={(option: Task | string) => typeof option === "string"
                ? option
                : "#" + option.id + " " + option.title}
            loading={options.loading && ready}
            noOptionsText={options.error || (ready ? "No matching tasks" : "Type a task number or 2 characters")}
            onChange={(_, value: Task | string | null) => {
                if (!value) return;

                setText("");
                input.current?.blur();

                if (typeof value === "string") {
                    if (value.trim()) navigate(ws.path("/search") + "?q=" + encodeURIComponent(value.trim()));
                    return;
                }

                open(value);
            }}
            renderOption={(params, option: Task) => {
                const { key, ...rest } = params as typeof params & { key: string };

                return (
                    <Box component="li" key={key} {...rest} sx={{ display: "block !important" }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">#{option.id}</Typography>
                            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{option.title}</Typography>
                            <ProjectCode code={option.projectCode} color={option.projectColor}
                                         done={!!option.completedAt} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" component="div" noWrap>
                            {[option.projectName, option.dueDate ? shortDate(option.dueDate) : ""]
                                .filter(v => !!v).join(" · ")}
                        </Typography>
                    </Box>
                );
            }}
            sx={{ width: 360 }}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={input}
                    placeholder="Search tasks"
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18, color: "rgba(255, 255, 255, 0.7)" }} />
                                </InputAdornment>
                            ),
                            // the shortcut shows while the field is empty, so it's seen
                            // before it's needed
                            endAdornment: (
                                <>
                                    {!text && (
                                        <InputAdornment position="end">
                                            <Box component="kbd" sx={{
                                                px: 0.75,
                                                borderRadius: 0.5,
                                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                                color: "rgba(255, 255, 255, 0.7)",
                                                fontFamily: "inherit",
                                                fontSize: 11,
                                                lineHeight: "18px",
                                            }}>
                                                {modKey} K
                                            </Box>
                                        </InputAdornment>
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                            sx: {
                                color: "inherit",
                                bgcolor: "rgba(255, 255, 255, 0.15)",
                                "& fieldset": { border: "none" },
                                "& input::placeholder": { color: "rgba(255, 255, 255, 0.7)", opacity: 1 },
                            },
                        },
                    }}
                />
            )}
        />
    );
}
